import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  adminApi,
  adminDepartmentsApi,
  adminScopesApi,
  RequestError,
  type AdminScopeItem,
  type DepartmentResponse,
} from '../api/client';
import {
  buildScopeUpdate,
  canEditScope,
  departmentsForChecklist,
  isBelowAnonymityThreshold,
  scopeKindOf,
  wouldSeeNothing,
  type AdminScopeKind,
} from '../utils/adminScopes';

type Editing = {
  userId: string;
  kind: AdminScopeKind;
  departmentIds: string[];
};

/**
 * 管理者の閲覧範囲（課題 #100）。
 * 全社管理者 / 拠点管理者を切り替え、拠点管理者が見られる部署を割り当てる。
 * 部署は階層を持たず、複数を選ぶ（2026-09-24 高尾さん決定の案 A）。
 * 操作できるのは全社管理者だけで、自分自身の行は変更できない（BE も 403）。
 */
export default function AdminScopes() {
  const { t } = useTranslation();
  const { getToken, user } = useAuth();
  const currentUserId = user?.userId ?? '';
  const [items, setItems] = useState<AdminScopeItem[]>([]);
  const [departments, setDepartments] = useState<DepartmentResponse[]>([]);
  /** 部署ごとの所属人数と匿名化の閾値。取れなければ注意書きを出さないだけ */
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});
  const [kAnonymity, setKAnonymity] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setForbidden(false);
    Promise.all([
      adminScopesApi.list(token),
      adminDepartmentsApi.list(token, { include_inactive: true }),
      adminApi.getOrganization(token).catch(() => null),
    ])
      .then(([scopes, depts, org]) => {
        setItems(Array.isArray(scopes) ? scopes : []);
        setDepartments(depts.data ?? []);
        if (org) {
          setKAnonymity(typeof org.k_anonymity === 'number' ? org.k_anonymity : null);
          setUserCounts(
            Object.fromEntries((org.departments ?? []).map((d) => [d.id, d.user_count]))
          );
        }
      })
      .catch((err) => {
        if (err instanceof RequestError && err.status === 403) {
          // 拠点管理者が開いた。理由が分かるように出す
          setForbidden(true);
        } else {
          setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        }
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const departmentName = useMemo(() => {
    const byId = new Map(departments.map((d) => [d.id, d.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [departments]);

  const startEdit = (item: AdminScopeItem) => {
    setNotice(null);
    setSaveError(null);
    setEditing({
      userId: item.user_id,
      kind: scopeKindOf(item.admin_scope),
      departmentIds: item.department_ids ?? [],
    });
  };

  const toggleDepartment = (id: string) => {
    setEditing((prev) =>
      prev
        ? {
            ...prev,
            departmentIds: prev.departmentIds.includes(id)
              ? prev.departmentIds.filter((v) => v !== id)
              : [...prev.departmentIds, id],
          }
        : prev
    );
  };

  const handleSave = async () => {
    if (!editing) return;
    const token = getToken();
    if (!token) return;
    // 部署 0 件の拠点管理者は何も見えなくなる。意図した操作かを確かめる
    if (
      wouldSeeNothing(editing.kind, editing.departmentIds) &&
      !window.confirm(t('admin.scopes.confirmSeesNothing'))
    ) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await adminScopesApi.update(
        editing.userId,
        buildScopeUpdate(editing.kind, editing.departmentIds),
        token
      );
      setEditing(null);
      setNotice(t('admin.scopes.saved'));
      load();
    } catch (err) {
      if (err instanceof RequestError && err.status === 403) setSaveError(t('admin.scopes.errorForbidden'));
      else if (err instanceof RequestError && err.status === 404) setSaveError(t('admin.scopes.errorNotFound'));
      else if (err instanceof RequestError && err.status === 422) setSaveError(t('admin.scopes.errorUnknownDepartment'));
      else setSaveError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const checklist = editing ? departmentsForChecklist(departments, editing.departmentIds) : [];

  return (
    <div className="page admin-scopes-page">
      <h1>{t('admin.scopes.title')}</h1>
      <p className="admin-page-intro">{t('admin.scopes.intro')}</p>

      {forbidden && (
        <div className="admin-card" role="alert" data-testid="admin-scopes-forbidden">
          <p>{t('admin.scopes.forbidden')}</p>
        </div>
      )}

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="admin-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {notice && <p className="admin-form-success" role="status">{notice}</p>}

      {!loading && !error && !forbidden && (
        <div className="admin-card">
          {items.length === 0 ? (
            <p className="admin-empty">{t('admin.scopes.empty')}</p>
          ) : (
            <table className="admin-table admin-scopes-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.scopes.columnAdmin')}</th>
                  <th scope="col">{t('admin.scopes.columnKind')}</th>
                  <th scope="col">{t('admin.scopes.columnRange')}</th>
                  <th scope="col">{t('admin.scopes.columnActions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const kind = scopeKindOf(item.admin_scope);
                  const editable = canEditScope(item.user_id, currentUserId);
                  const names =
                    item.department_names && item.department_names.length > 0
                      ? item.department_names
                      : (item.department_ids ?? []).map(departmentName);
                  return (
                    <tr key={item.user_id} data-testid="admin-scope-row">
                      <td>{item.email || item.user_id}</td>
                      <td>{t(`admin.scopes.kind.${kind}`)}</td>
                      <td>
                        {kind === 'all' ? t('admin.scopes.rangeAll') : names.join('、')}
                        {/* 割り当ての消し忘れは「何も見えない」として現れる。一覧で気づけるようにする */}
                        {item.sees_nothing && (
                          <p className="admin-scopes-warning" role="alert" data-testid="admin-scope-sees-nothing">
                            {t('admin.scopes.seesNothing')}
                          </p>
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <button
                            type="button"
                            className="admin-btn-text"
                            onClick={() => startEdit(item)}
                            disabled={saving}
                          >
                            {t('admin.scopes.edit')}
                          </button>
                        ) : (
                          <span className="admin-form-hint" data-testid="admin-scope-self">
                            {t('admin.scopes.selfNotEditable')}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editing && (
        <div className="admin-card admin-form admin-scopes-editor" data-testid="admin-scope-editor">
          <h2>
            {t('admin.scopes.editTitle', {
              name: items.find((i) => i.user_id === editing.userId)?.email || editing.userId,
            })}
          </h2>
          <fieldset className="admin-scopes-kind">
            <legend>{t('admin.scopes.columnKind')}</legend>
            {(['all', 'departments'] as const).map((k) => (
              <label key={k}>
                <input
                  type="radio"
                  name="admin-scope-kind"
                  value={k}
                  checked={editing.kind === k}
                  onChange={() => setEditing({ ...editing, kind: k })}
                />
                {t(`admin.scopes.kindOption.${k}`)}
              </label>
            ))}
          </fieldset>

          {editing.kind === 'departments' && (
            <fieldset className="admin-scopes-departments">
              <legend>{t('admin.scopes.departmentsLegend')}</legend>
              {checklist.length === 0 ? (
                <p className="admin-empty">{t('admin.scopes.noDepartments')}</p>
              ) : (
                checklist.map((d) => {
                  const count = userCounts[d.id];
                  return (
                    <label key={d.id} className="admin-scopes-department">
                      <input
                        type="checkbox"
                        checked={editing.departmentIds.includes(d.id)}
                        onChange={() => toggleDepartment(d.id)}
                      />
                      <span>{d.name}</span>
                      {count != null && (
                        <span className="admin-form-hint">{t('admin.scopes.userCount', { count })}</span>
                      )}
                      {d.status === 'inactive' && (
                        <span className="admin-form-hint">{t('admin.scopes.inactive')}</span>
                      )}
                      {isBelowAnonymityThreshold(count, kAnonymity) && (
                        <span className="admin-form-hint" data-testid="admin-scope-small-department">
                          {t('admin.scopes.belowThreshold', { k: kAnonymity })}
                        </span>
                      )}
                    </label>
                  );
                })
              )}
              {wouldSeeNothing(editing.kind, editing.departmentIds) && (
                <p className="admin-scopes-warning" role="alert" data-testid="admin-scope-editor-sees-nothing">
                  {t('admin.scopes.editorSeesNothing')}
                </p>
              )}
            </fieldset>
          )}

          {saveError && <p className="admin-form-error" role="alert">{saveError}</p>}
          <div className="admin-scopes-editor-actions">
            <button type="button" className="admin-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t('common.loading') : t('admin.scopes.save')}
            </button>
            <button type="button" className="admin-btn-text" onClick={() => setEditing(null)} disabled={saving}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
